import React from "react";
import { css } from "@emotion/react";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import { colors, margins } from "style";
import { MUTATION_TYPE, useToast } from "hooks";
import { UPDATE_PROFILE } from "graphql/mutations";
import { ModalForm, MODAL_SIZE } from "components/Modal";
import { SIZE, TYPE } from "components/Form";

/**
 * EmailChangeModal - Modal for changing user email address
 *
 * @param {function} onClose
 * @param {string}   updateId
 * @param {Object}   mutationData      Mutation that saves the new email, defaults to updating the profile
 * @param {string}   parentObjectKey   Key the email is nested under in the mutation params, if any
 * @param {string}   successMessageId  Toast shown after the change is submitted
 */
const EmailChangeModal = ({
  onClose,
  updateId,
  mutationData = { mutationName: { update: UPDATE_PROFILE } },
  parentObjectKey,
  successMessageId = "useProfile.UpdateSuccess",
  ...props
}) => {
  const { toast } = useToast();

  const handleCompleted = () => {
    onClose();
    toast.success(<FormattedMessage id={successMessageId} />);
  };

  return (
    <ModalForm
      mutationType={MUTATION_TYPE.update}
      updateId={updateId}
      mutationData={mutationData}
      onCompleted={handleCompleted}
      onClose={onClose}
      title={<FormattedMessage id="Profile.EmailChange.Modal.Title" />}
      description={
        <div css={styles.description}>
          <FormattedMessage id="Profile.EmailChange.Modal.Description" />
        </div>
      }
      data={FORM_DATA(parentObjectKey)}
      size={MODAL_SIZE.sm}
      primaryButtonMessageId="Global.Submit"
      disclaimer={
        <div css={styles.disclaimer}>
          <FormattedMessage id="Profile.EmailChange.Modal.Disclaimer" />
        </div>
      }
      {...props}
    />
  );
};

const FORM_DATA = (parentObjectKey) => [
  {
    style: css`
      width: 100%;
    `,
    items: [
      {
        label: "New Email Address",
        type: TYPE.input,
        size: SIZE.xlarge,
        parentObjectKey,
        properties: {
          required: true,
          name: "email",
          type: "email",
          placeholder: "Enter new email address",
          autoFocus: true,
        },
      },
    ],
  },
];

const styles = {
  description: css`
    margin-bottom: ${margins.xxsmall};
    color: ${colors.grayAnatomyLight1};
    line-height: 1.4;
  `,

  disclaimer: css`
    font-style: italic;
    margin-top: ${margins.xsmall};
    color: ${colors.grayAnatomyLight1};
    line-height: 1.4;
  `,
};

EmailChangeModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  updateId: PropTypes.string.isRequired,
  mutationData: PropTypes.object,
  parentObjectKey: PropTypes.string,
  successMessageId: PropTypes.string,
};

export default EmailChangeModal;
